import React, { createContext, useMemo, useReducer } from 'react';
import { VIEWPORT_BREAKPOINT_PX } from '@constants/common';
import { Action, TChildren } from '@models/common';

export enum DeviceType {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

export type HumanizerVersion = 'v1' | 'v2';

const INITIAL_HUMANIZER_VERSION = 'v1' as HumanizerVersion;

const getDeviceType = () => {
  // Use matchMedia (not window.innerWidth) so JS device detection is measured the
  // same way as the CSS media queries. innerWidth includes the vertical scrollbar
  // width, while @media does not — that mismatch made isDesktop disagree with the
  // CSS `@md` breakpoint in a scrollbar-wide sliver, rendering desktop-only markup
  // under a mobile layout. The max-width boundaries below mirror the SCSS
  // `$breakpoints-maw` map (sm: 767px, md: 1023px).
  if (typeof window === 'undefined' || !window.matchMedia) {
    return DeviceType.DESKTOP;
  }

  if (
    window.matchMedia(`(max-width: ${VIEWPORT_BREAKPOINT_PX.SM - 1}px)`).matches
  ) {
    return DeviceType.MOBILE;
  }

  if (
    window.matchMedia(`(max-width: ${VIEWPORT_BREAKPOINT_PX.MD - 1}px)`).matches
  ) {
    return DeviceType.TABLET;
  }

  return DeviceType.DESKTOP;
};

type Props = {
  children: TChildren;
};

type State = {
  deviceType: DeviceType;
  humanizerVersion: HumanizerVersion;
};

type AppContextValue = {
  appState: State;
  appDispatch: React.Dispatch<Action<ActionType>>;
};

const initialState: State = {
  deviceType: getDeviceType(),
  humanizerVersion: INITIAL_HUMANIZER_VERSION,
};

enum ActionType {
  SET_DEVICE_TYPE = 'SET_DEVICE_TYPE',
  SET_HUMANIZER_VERSION = 'SET_HUMANIZER_VERSION',
}

export const ActionCreator = {
  setDeviceType: () => ({
    type: ActionType.SET_DEVICE_TYPE,
    payload: getDeviceType(),
  }),
  setHumanizerVersion: (version: HumanizerVersion) => ({
    type: ActionType.SET_HUMANIZER_VERSION,
    payload: version,
  }),
};

const reducer = (state: State, action: Action<ActionType>): State => {
  switch (action.type) {
    case ActionType.SET_DEVICE_TYPE: {
      const nextDeviceType = action.payload as DeviceType;

      if (state.deviceType === nextDeviceType) {
        return state;
      }

      return { ...state, deviceType: nextDeviceType };
    }
    case ActionType.SET_HUMANIZER_VERSION: {
      if (INITIAL_HUMANIZER_VERSION === 'v2') {
        return state;
      }

      const nextHumanizerVersion = action.payload as HumanizerVersion;

      if (state.humanizerVersion === nextHumanizerVersion) {
        return state;
      }

      return { ...state, humanizerVersion: nextHumanizerVersion };
    }
    default:
      return state;
  }
};

export const AppContext = createContext<AppContextValue>({
  appState: initialState,
  appDispatch: () => {},
});

const AppProvider: React.FC<Props> = ({ children }) => {
  const [appState, appDispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => ({ appState, appDispatch }), [appState]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppProvider;
